import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'project' | 'request' | 'message' | 'payment' | 'system';
  reference_id?: string;
  reference_type?: 'project' | 'request' | 'message' | 'user';
  is_read: boolean;
  created_at: string;
}

const Notifications: React.FC = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  // Load user data
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (!token || !userData) {
          navigate('/signin');
          return;
        }

        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error loading user data:', error);
        navigate('/signin');
      }
    };

    loadUserData();
  }, [navigate]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `http://localhost:5000/api/projects/notifications?unread_only=${filter === 'unread' ? 'true' : 'false'}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setNotifications(response.data.data.notifications);
        setUnreadCount(response.data.data.unreadCount);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [filter]); // Added filter as dependency

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user, fetchNotifications]); // Added fetchNotifications as dependency

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `http://localhost:5000/api/projects/notifications/${notificationId}/read`,
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Update local state
      setNotifications(prev => prev.map(notif => 
        notif.id === notificationId ? { ...notif, is_read: true } : notif
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        'http://localhost:5000/api/projects/notifications/read-all',
        {},
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      // Update local state
      setNotifications(prev => prev.map(notif => ({ ...notif, is_read: true })));
      setUnreadCount(0);
      alert('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId: string) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    // Note: You would need a DELETE endpoint for this
    console.log('Delete notification:', notificationId);
    // In a real app: await axios.delete(`/api/notifications/${notificationId}`);
    
    // Update local state
    setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'project': return '📋';
      case 'request': return '📨';
      case 'message': return '💬';
      case 'payment': return '💰';
      case 'system': return '⚙️';
      default: return '🔔';
    }
  };

  const getNotificationLink = (notification: Notification) => {
    if (!notification.reference_id) return null;

    switch (notification.reference_type) {
      case 'project':
        return `/${user.role}/projects/${notification.reference_id}`;
      case 'message':
        return `/messages/${notification.reference_id}`;
      case 'request':
        return user.role === 'client' 
          ? `/client/projects/${notification.reference_id}/requests`
          : `/freelancer/requests`;
      default:
        return null;
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: 'numeric'
      });
    }
  };

  if (!user) {
    return null;
  }

  const filteredNotifications = filter === 'unread' 
    ? notifications.filter(n => !n.is_read)
    : notifications;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-brand">
            <div className="brand-logo">NS</div>
            <span className="brand-text">NexSync</span>
          </div>
          
          <div className="navbar-actions">
            <div className="user-info">
              <div className="user-avatar">
                {user.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="user-details">
                <span className="user-name">{user.username.toUpperCase()}</span>
                <div className="user-meta">
                  <span className={`role-badge role-${user.role}`}>{user.role}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="dashboard-container">
        <div className="dashboard-content">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Notifications</h1>
                <p className="text-gray-600">
                  Stay updated with your projects and activities
                </p>
              </div>
              <div className="flex items-center gap-4">
                {unreadCount > 0 && (
                  <button 
                    type="button"
                    onClick={handleMarkAllAsRead}
                    className="btn btn-secondary"
                  >
                    Mark All as Read
                  </button>
                )}
                <button 
                  type="button"
                  onClick={() => navigate('/dashboard')} 
                  className="btn btn-secondary"
                  title="Back to Dashboard"
                >
                  Dashboard
                </button>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-gray-900">{notifications.length}</div>
                  <div className="text-sm text-gray-600">Total Notifications</div>
                </div>
                <div className="text-3xl">📊</div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-red-600">{unreadCount}</div>
                  <div className="text-sm text-gray-600">Unread Notifications</div>
                </div>
                <div className="text-3xl">🔔</div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-2xl font-bold text-blue-600">
                    {notifications.filter(n => n.type === 'project').length}
                  </div>
                  <div className="text-sm text-gray-600">Project Updates</div>
                </div>
                <div className="text-3xl">📋</div>
              </div>
            </div>
          </div>

          {/* Filters and Notifications List */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            {/* Filters */}
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFilter('all')}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      filter === 'all'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    All Notifications
                  </button>
                  <button
                    type="button"
                    onClick={() => setFilter('unread')}
                    className={`px-4 py-2 rounded-lg font-medium ${
                      filter === 'unread'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Unread Only
                    {unreadCount > 0 && (
                      <span className="ml-2 bg-red-500 text-white text-xs font-bold rounded-full px-2 py-1">
                        {unreadCount}
                      </span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Notifications List */}
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="loading-spinner mx-auto mb-4"></div>
                <p className="text-gray-600">Loading notifications...</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-gray-400 text-4xl mb-4">
                  {filter === 'unread' ? '✅' : '📭'}
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {filter === 'unread' ? 'No Unread Notifications' : 'No Notifications'}
                </h3>
                <p className="text-gray-600">
                  {filter === 'unread' 
                    ? 'You have read all your notifications.' 
                    : 'You don\'t have any notifications yet.'}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {filteredNotifications.map((notification) => {
                  const link = getNotificationLink(notification);
                  
                  return (
                    <div 
                      key={notification.id}
                      className={`p-6 transition-colors duration-200 ${
                        !notification.is_read ? 'bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl ${
                            !notification.is_read ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {getNotificationIcon(notification.type)}
                          </div>
                          
                          <div className="flex-1">
                            <div className="flex items-start justify-between mb-2">
                              <h3 className={`font-semibold ${
                                !notification.is_read ? 'text-blue-900' : 'text-gray-900'
                              }`}>
                                {notification.title}
                              </h3>
                              <div className="flex items-center gap-2">
                                <div className="text-sm text-gray-500">
                                  {formatTime(notification.created_at)}
                                </div>
                                {!notification.is_read && (
                                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                                )}
                              </div>
                            </div>
                            
                            <p className={`mb-3 ${
                              !notification.is_read ? 'text-blue-700' : 'text-gray-700'
                            }`}>
                              {notification.message}
                            </p>
                            
                            <div className="flex items-center gap-3">
                              {link && (
                                <Link
                                  to={link}
                                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                                  onClick={() => handleMarkAsRead(notification.id)}
                                >
                                  View Details →
                                </Link>
                              )}
                              {!notification.is_read && (
                                <button
                                  type="button"
                                  onClick={() => handleMarkAsRead(notification.id)}
                                  className="text-sm text-gray-600 hover:text-gray-800"
                                >
                                  Mark as Read
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleDeleteNotification(notification.id)}
                                className="text-sm text-red-600 hover:text-red-800 ml-auto"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Tips */}
          <div className="mt-8 p-6 bg-gray-50 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Notification Tips</h3>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-center gap-2">
                <span className="text-blue-600">📋</span>
                <span>Project notifications: Updates about your projects</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-600">📨</span>
                <span>Request notifications: New project requests or status changes</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-purple-600">💬</span>
                <span>Message notifications: New messages from clients/freelancers</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-yellow-600">💰</span>
                <span>Payment notifications: Payment updates and releases</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notifications;